import { ReactNode } from 'react';
import { Plus, Minus, Info } from 'lucide-react';
import { twMerge } from 'tailwind-merge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components';

interface FilterSectionProps {
  title: string;
  selectedCount: number;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  children: ReactNode;
  tooltip?: string;
}

export const FilterSection = ({
  title,
  selectedCount,
  isOpen,
  onOpenChange,
  children,
  tooltip,
}: FilterSectionProps) => {
  return (
    <Collapsible open={isOpen} onOpenChange={onOpenChange}>
      <div className="flex items-center gap-2">
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className={twMerge(
              'flex w-full items-center justify-between gap-2 rounded-md',
              'outline-2 outline-offset-[3px]',
              'focus-visible:outline focus-visible:outline-magenta',
            )}
          >
            <div className="flex items-center gap-2">
              <h2 className="text-left font-effra text-lg font-bold">{title}</h2>
              {tooltip && (
                <Dialog>
                  <DialogTrigger
                    className="rounded-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[3px]
                      focus-visible:outline-magenta"
                    title="Learn about study levels"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Info className="size-6" />
                  </DialogTrigger>
                  <DialogContent className="border-none bg-white p-0 md:max-w-3xl md:[&>button]:right-7 md:[&>button]:top-7">
                    <DialogHeader className="max-h-[90vh] space-y-0 text-left">
                      <div className="p-5 md:p-8">
                        <DialogTitle asChild>
                          <h2 className="text-[22px]">Study Levels</h2>
                        </DialogTitle>
                      </div>
                      <div className="overflow-auto rounded-lg bg-[#f7f7f7] p-5 md:p-8">
                        <DialogDescription asChild>
                          <div
                            aria-description="Study levels"
                            className="space-y-4 [&>h3]:text-[1.75rem] [&>h3]:font-bold md:[&>h3]:text-[34px] [&>p]:text-base [&>p]:text-[#4b4b4b]"
                            dangerouslySetInnerHTML={{ __html: tooltip }}
                          />
                        </DialogDescription>
                      </div>
                    </DialogHeader>
                  </DialogContent>
                </Dialog>
              )}
            </div>

            <div className="flex items-center gap-2">
              <div
                className={twMerge(
                  'hidden md:flex h-4 w-4 shrink-0 items-center justify-center text-xs font-medium transition-all',
                  'translate-y-0.25 rounded-full bg-secondary-grey-05',
                  selectedCount > 0 && !isOpen ? 'opacity-1' : 'translate-x-2 opacity-0',
                )}
              >
                {selectedCount}
              </div>

              <div className="relative flex size-8 shrink-0 items-center justify-center rounded-full bg-secondary-grey-05">
                <Plus
                  className={twMerge(
                    'absolute inset-auto size-[19px] transition-all duration-300',
                    isOpen ? 'rotate-90 scale-0 opacity-0' : 'rotate-0 opacity-100',
                  )}
                />
                <Minus
                  className={twMerge(
                    'absolute inset-auto size-[19px] transition-all duration-300',
                    isOpen ? 'rotate-0 opacity-100' : '-rotate-90 opacity-0',
                  )}
                />
              </div>
            </div>
          </button>
        </CollapsibleTrigger>
      </div>
      <CollapsibleContent className="px-[3px] my-5 sm:my-0">
        <div className="mt-4">{children}</div>
      </CollapsibleContent>
    </Collapsible>
  );
};
